// ============================================================================
// MODULE 9 — Working-days calendar (pure functions)
// ============================================================================
//
// V2 semantics (spec §2): working days per day-of-week for a month =
// Mon..Sat dates that are not inside a non-working range; Sunday is NEVER
// a working day. Non-working ranges come from the `holidays` collection
// (vacation/holiday/no_school — all are non-working).
//
// TZ safety: everything works on ISO DATE STRINGS ("YYYY-MM-DD") — callers
// convert Timestamps to date strings in Europe/Vienna; the engine never
// constructs local Dates from strings.
// ============================================================================

import type { MonthKey } from '../../types/module9';

/** Inclusive non-working date range (ISO date strings "YYYY-MM-DD"). */
export interface NonWorkingRange {
  startDate: string;
  endDate: string;
}

/** ISO day-of-week: 1=Mon … 6=Sat (Sunday is excluded by definition). */
export type WorkingDow = 1 | 2 | 3 | 4 | 5 | 6;

export interface MonthWorkingCalendar {
  month: MonthKey;
  /** Брой РАБОТНИ дати в месеца за всеки ден от седмицата (1=Пон … 6=Съб). */
  workingDaysByDow: Record<WorkingDow, number>;
  totalWorkingDays: number;
}

/** Български ден от седмицата → ISO dow (за легаси разписанието). */
export const BG_DAY_TO_DOW: Record<string, WorkingDow> = {
  'понеделник': 1,
  'вторник': 2,
  'сряда': 3,
  'четвъртък': 4,
  'петък': 5,
  'събота': 6,
};

/** Английски (ScheduleSlot конвенцията, lowercase) → ISO dow. */
export const EN_DAY_TO_DOW: Record<string, WorkingDow> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export function dayNameToDow(day: string): WorkingDow | undefined {
  const k = String(day || '').trim().toLowerCase();
  return BG_DAY_TO_DOW[k] ?? EN_DAY_TO_DOW[k];
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Работният календар за месец: за всяка дата Пон–Съб, която не попада в
 * неработен диапазон, брои се към деня ѝ от седмицата. Чиста функция —
 * повредени диапазони (липсващи дати) се игнорират (не чупят сметката).
 */
export function monthWorkingCalendar(
  month: MonthKey,
  nonWorking: NonWorkingRange[]
): MonthWorkingCalendar {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) throw new Error(`Invalid month key: ${month}`);
  const year = Number(m[1]);
  const mon = Number(m[2]); // 1..12
  if (mon < 1 || mon > 12) throw new Error(`Invalid month key: ${month}`);

  const ranges = nonWorking.filter((r) => r && r.startDate && r.endDate);
  const daysInMonth = new Date(Date.UTC(year, mon, 0)).getUTCDate();

  const byDow: Record<WorkingDow, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  let total = 0;

  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${pad2(mon)}-${pad2(d)}`;
    const dow = new Date(Date.UTC(year, mon - 1, d)).getUTCDay(); // 0=Sun
    if (dow === 0) continue; // неделя — никога работна
    const isNonWorking = ranges.some((r) => iso >= r.startDate && iso <= r.endDate);
    if (isNonWorking) continue;
    byDow[dow as WorkingDow]++;
    total++;
  }

  return { month, workingDaysByDow: byDow, totalWorkingDays: total };
}
